import { canOpenResource } from '../../access';
import { policyAllows } from '../../modules';
import type { Command } from '../../service';
import {
  campuses,
  type Entity,
  type Kind,
  type Snapshot,
  teachers,
} from '../../types';

export const capabilityKeys = [
  '录音读取',
  '远程控制',
  '定位',
  '配置下发',
  '固件升级',
  '补传',
];
export const lifecycleLabels: Record<string, string> = {
  stock: '在库',
  assigned: '领用',
  repair: '维修',
  disabled: '停用',
  retired: '报废',
};
export const operationLabels: Record<string, string> = {
  save: '设备建档',
  assign: '领用',
  borrow: '借用',
  return: '归还',
  transfer: '调拨',
  replace: '替换',
  disable: '停用',
  enable: '启用',
  retire: '报废',
  remote: '模拟远程配置',
  report: '报修',
  repair: '维修处理',
  inventory: '库存操作',
  capability: '能力版本',
};
export interface CapabilityVersion {
  uuid: string;
  version: number;
  at: string;
  note: string;
  values: Record<string, string>;
}
export interface Movement {
  uuid: string;
  at: string;
  operation: string;
  quantity: number;
  before: number;
  after: number;
  reserved: number;
  reason: string;
  commandUuid: string;
}
export interface Helpers {
  append(
    s: Snapshot,
    kind: Kind,
    title: string,
    extra?: Partial<Entity>,
  ): Entity;
  get(s: Snapshot, uuid: string | undefined, kind?: Kind): Entity;
  touch(row: Entity, s: Snapshot): void;
}
export class DeviceError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
  }
}
function reject(message: string, code = 'VALIDATION'): never {
  throw new DeviceError(code, message);
}
const required = (value: string | undefined, name: string) =>
  value?.trim() || reject(`请填写${name}`);
export const serialNumber = (e: Entity) =>
  e.fields?.serialNumber || `SN${e.uuid.slice(-8)}`;
export function versions(model: Entity): CapabilityVersion[] {
  return JSON.parse(model.fields?.capabilityVersions || '[]');
}
export function capabilities(
  model: Entity | undefined,
): Record<string, string> {
  if (!model)
    return Object.fromEntries(capabilityKeys.map((k) => [k, '待验证']));
  const version = versions(model).find(
    (v) => v.uuid === model.fields?.publishedVersion,
  );
  return (
    version?.values ||
    Object.fromEntries(
      capabilityKeys.map((k) => [k, model.fields?.[k] || '待验证']),
    )
  );
}
export function bindingAt(bindings: Entity[], uuid: string, at: string) {
  const time = Date.parse(at);
  if (!Number.isFinite(time)) reject('发生时间无效');
  const matches = bindings.filter(
    (b) =>
      b.deviceUuid === uuid &&
      b.startAt &&
      Date.parse(b.startAt) <= time &&
      (!b.endAt || Date.parse(b.endAt) > time),
  );
  if (matches.length > 1) reject('历史绑定存在重叠，需核实后更正', 'CONFLICT');
  return matches[0];
}
export function health(device: Entity, now: string) {
  const reasons: string[] = [];
  const elapsed = device.reportedAt
    ? Date.parse(now) - Date.parse(device.reportedAt)
    : NaN;
  const communication =
    !Number.isFinite(elapsed) || elapsed < 0
      ? '未知'
      : elapsed > 30 * 60 * 1000
        ? '离线'
        : '在线';
  if (communication !== '在线') reasons.push(`上报${communication}`);
  if (device.battery !== undefined && device.battery < 20)
    reasons.push('电量低于20%');
  if (device.status === 'repair') reasons.push('维修中');
  if (Number(device.fields?.pendingFiles) > 0)
    reasons.push(`${device.fields?.pendingFiles}个文件待上传`);
  if (
    device.fields?.storageFreeBytes &&
    Number(device.fields.storageFreeBytes) < 1024 ** 3
  )
    reasons.push('可用存储低于1GB');
  return { communication, reasons };
}
export const availableQuantity = (item: Entity) =>
  Number(item.fields?.available ?? item.fields?.可用数量 ?? 0);
export const stockMovements = (item: Entity): Movement[] =>
  JSON.parse(item.fields?.movements || '[]');
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const source = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < source.length; i++) {
    const ch = source[i];
    if (ch === '"') {
      if (quoted && source[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (!quoted && cell) reject('CSV引号位置无效');
      else quoted = !quoted;
    } else if (ch === ',' && !quoted) {
      row.push(cell);
      cell = '';
    } else if ((ch === '\n' || ch === '\r') && !quoted) {
      row.push(cell);
      if (row.some((v) => v.trim())) rows.push(row);
      row = [];
      cell = '';
      if (ch === '\r' && source[i + 1] === '\n') i++;
    } else cell += ch;
  }
  if (quoted) reject('CSV引号未闭合');
  row.push(cell);
  if (row.some((v) => v.trim())) rows.push(row);
  return rows;
}
export function csvCell(value: string) {
  return `"${(/^[=+@\-\t\r]/.test(value) ? `'${value}` : value).replaceAll('"', '""')}"`;
}
export function checkSerial(
  entities: Entity[],
  serial: string,
  except?: string,
) {
  if (
    entities.some(
      (e) =>
        e.kind === 'device' &&
        e.uuid !== except &&
        serialNumber(e).toUpperCase() === serial.trim().toUpperCase(),
    )
  )
    reject('序列号重复，未保存', 'CONFLICT');
}
export function pendingCollectionCount(s: Snapshot, uuid: string): number {
  const old = s.entities.filter(
    (e) =>
      e.kind === 'batch' &&
      e.deviceUuid === uuid &&
      !['completed', 'cancelled'].includes(e.status),
  ).length;
  const collection = (
    s as Snapshot & {
      collectionV1?: { files: { deviceUuid?: string; stage: string }[] };
    }
  ).collectionV1;
  return (
    old +
    (collection?.files.filter(
      (f) =>
        f.deviceUuid === uuid &&
        !['completed', 'duplicate', 'cancelled'].includes(f.stage),
    ).length || 0)
  );
}
function campusAllowed(s: Snapshot, campus: string) {
  if (!campuses.includes(campus)) reject('校区不存在');
  if (s.context.campus !== '全部校区' && s.context.campus !== campus)
    reject('目标校区超出当前授权范围', 'FORBIDDEN');
}
export function deviceCommand(s: Snapshot, input: Command, h: Helpers): Entity {
  const f = input.fields || {};
  const op = f.op;
  const target = input.uuid ? h.get(s, input.uuid) : undefined;
  const resource =
    op === 'capability'
      ? 'deviceModel'
      : op === 'inventory' || op === 'inventoryCreate'
        ? 'inventory'
        : op === 'repair'
          ? 'repair'
          : 'device';
  const kind = resource === 'deviceModel' ? 'model' : resource;
  const roles =
    op === 'capability'
      ? ['deviceManager', 'campusManager', 'groupOperator', 'systemAdmin']
      : [
          'deviceManager',
          'campusManager',
          'groupOperator',
          ...(op === 'repair' ? ['support'] : []),
        ];
  if (
    !roles.includes(s.context.role) ||
    !canOpenResource(resource, s) ||
    !policyAllows(s, kind as Kind, 'write')
  )
    reject('当前视角没有此操作权限', 'FORBIDDEN');
  if (f.expiresAt && Date.now() > Number(f.expiresAt))
    reject('预览已过期，请重新预览', 'CONFLICT');
  const relatedKinds: Kind[] = [
    'assign',
    'borrow',
    'return',
    'transfer',
    'replace',
  ].includes(op)
    ? ['binding']
    : op === 'report'
      ? ['binding', 'repair', 'ticket']
      : op === 'repair'
        ? ['device', 'ticket']
        : [];
  if (relatedKinds.some((k) => !policyAllows(s, k, 'write')))
    reject('关联资源的写入权限不足', 'FORBIDDEN');
  const event = (row: Entity, note: string) => {
    row.fields = {
      ...row.fields,
      events: JSON.stringify([
        ...JSON.parse(row.fields?.events || '[]'),
        {
          at: s.now,
          operation: operationLabels[op] || op,
          note,
          memberUuid: s.context.viewMemberUuid,
        },
      ]),
    };
  };
  const device = () =>
    target?.kind === 'device' ? target : reject('请选择设备');
  const bind = (
    row: Entity,
    memberUuid: string,
    campus: string,
    at: string,
    kind: string,
  ) => {
    if (!teachers.includes(memberUuid)) reject('请选择统一目录中的使用人');
    campusAllowed(s, campus);
    const start = Date.parse(at);
    if (!Number.isFinite(start) || start > Date.parse(s.now))
      reject('生效时间须有效且不晚于当前业务时间');
    if (
      s.entities.some(
        (b) =>
          b.kind === 'binding' &&
          b.deviceUuid === row.uuid &&
          (!b.endAt || Date.parse(b.endAt) > start),
      )
    )
      reject('设备存在重叠绑定区间', 'CONFLICT');
    h.append(s, 'binding', `${row.title}${operationLabels[kind] || kind}`, {
      deviceUuid: row.uuid,
      campus,
      memberUuid,
      startAt: new Date(start).toISOString(),
      status: 'active',
      note: required(input.note, '原因'),
      fields: {
        bindingKind: kind,
        personSnapshot: memberUuid,
        campusSnapshot: campus,
      },
    });
    row.status = 'assigned';
    row.memberUuid = memberUuid;
    row.campus = campus;
  };
  const endBinding = (row: Entity, at: string) => {
    const b = bindingAt(
      s.entities.filter((e) => e.kind === 'binding'),
      row.uuid,
      at,
    );
    if (!b || !b.startAt || Date.parse(at) < Date.parse(b.startAt))
      reject('没有可结束的有效绑定，或生效时间早于领用时间', 'CONFLICT');
    b.endAt = at;
    b.status = 'completed';
    h.touch(b, s);
    row.status = 'stock';
  };
  if (op === 'save') {
    const serial = required(f.serialNumber, '序列号');
    checkSerial(s.entities, serial, target?.uuid);
    campusAllowed(s, f.campus);
    const model = h.get(s, f.modelUuid, 'model');
    if (target && target.kind !== 'device') reject('对象类型错误');
    if (
      target &&
      (target.campus !== f.campus || target.model !== model.title) &&
      target.status !== 'stock'
    )
      reject('仅在库设备可修改型号/校区；领用设备请使用调拨');
    const row =
      target ||
      h.append(s, 'device', required(input.title, '设备名称'), {
        status: 'stock',
        campus: f.campus,
        model: model.title,
      });
    row.title = required(input.title, '设备名称');
    row.campus = f.campus;
    row.model = model.title;
    row.fields = {
      ...row.fields,
      serialNumber: serial,
      modelUuid: model.uuid,
      purchaseAt: f.purchaseAt || '',
      warrantyUntil: f.warrantyUntil || '',
    };
    event(row, target ? '编辑设备档案' : '新设备建档入库');
    return row;
  }
  if (op === 'inventoryCreate') {
    campusAllowed(s, f.campus);
    return h.append(s, 'inventory', required(input.title, '配件名称'), {
      campus: f.campus,
      status: 'active',
      fields: {
        available: '0',
        reserved: '0',
        unit: '件',
        itemType: 'accessory',
        movements: '[]',
      },
    });
  }
  if (op === 'inventory') {
    if (target?.kind !== 'inventory') reject('请选择库存');
    const quantity = Number(f.quantity);
    const before = availableQuantity(target);
    const reserved = Number(target.fields?.reserved || 0);
    if (
      !Number.isSafeInteger(quantity) ||
      quantity < (f.movement === 'adjust' ? 0 : 1)
    )
      reject('数量须为有效整数');
    const reason = required(input.note, '库存原因');
    let after = before;
    let occupied = reserved;
    if (f.movement === 'in') after += quantity;
    else if (f.movement === 'out') {
      if (quantity > before) reject('库存不足，未生成流水');
      after -= quantity;
      occupied += quantity;
    } else if (f.movement === 'return') {
      if (quantity > reserved) reject('归还超过占用数量');
      after += quantity;
      occupied -= quantity;
    } else if (f.movement === 'adjust') after = quantity;
    else reject('库存动作无效');
    if (!Number.isSafeInteger(after) || !Number.isSafeInteger(occupied))
      reject('库存数量超出有效范围');
    const movement: Movement = {
      uuid: crypto.randomUUID(),
      at: s.now,
      operation: f.movement,
      quantity,
      before,
      after,
      reserved: occupied,
      reason,
      commandUuid: input.meta?.commandUuid || '',
    };
    target.fields = {
      ...target.fields,
      available: String(after),
      可用数量: String(after),
      reserved: String(occupied),
      movements: JSON.stringify([...stockMovements(target), movement]),
    };
    return target;
  }
  if (op === 'capability') {
    if (target?.kind !== 'model') reject('请选择型号');
    if (
      ['publish', 'rollback'].includes(f.stage) &&
      s.context.campus !== '全部校区' &&
      s.entities.some(
        (e) =>
          e.kind === 'device' &&
          e.model === target.title &&
          e.campus !== s.context.campus,
      )
    )
      reject('型号影响多个校区，请在全部授权校区范围下发布', 'FORBIDDEN');
    const history = versions(target);
    if (f.stage === 'draft') {
      const values = JSON.parse(f.values) as Record<string, string>;
      if (
        !capabilityKeys.every((k) =>
          ['已验证', '待验证', '不支持'].includes(values[k]),
        )
      )
        reject('能力状态无效');
      target.fields = {
        ...target.fields,
        capabilityDraft: JSON.stringify(values),
        verificationNote: required(input.note, '验证材料及说明'),
        verifiedAt: '',
        testedDraft: '',
      };
    } else if (f.stage === 'test') {
      if (!target.fields?.capabilityDraft || !target.fields.verificationNote)
        reject('请先保存草稿及验证材料');
      target.fields = {
        ...target.fields,
        verifiedAt: s.now,
        testedDraft: target.fields.capabilityDraft,
      };
    } else if (f.stage === 'publish') {
      if (
        !target.fields?.capabilityDraft ||
        target.fields.testedDraft !== target.fields.capabilityDraft
      )
        reject('草稿未通过模拟验证');
      const v: CapabilityVersion = {
        uuid: crypto.randomUUID(),
        version: history.length + 1,
        at: s.now,
        note: target.fields.verificationNote || '',
        values: JSON.parse(target.fields.capabilityDraft),
      };
      target.fields = {
        ...target.fields,
        capabilityVersions: JSON.stringify([...history, v]),
        publishedVersion: v.uuid,
        ...v.values,
      };
      for (const d of s.entities.filter(
        (e) => e.kind === 'device' && e.model === target.title,
      )) {
        d.supportsRemote = v.values.远程控制 === '已验证';
        d.fields = { ...d.fields, capabilityVersionUuid: v.uuid };
        h.touch(d, s);
      }
    } else if (f.stage === 'rollback') {
      const previous = history.find((v) => v.uuid === f.versionUuid);
      if (!previous) reject('历史版本不存在');
      const v = {
        ...previous,
        uuid: crypto.randomUUID(),
        version: history.length + 1,
        at: s.now,
        note: `回退至版本${previous.version}：${required(input.note, '回退原因')}`,
      };
      target.fields = {
        ...target.fields,
        ...v.values,
        publishedVersion: v.uuid,
        capabilityVersions: JSON.stringify([...history, v]),
      };
      for (const d of s.entities.filter(
        (e) => e.kind === 'device' && e.model === target.title,
      )) {
        d.supportsRemote = v.values.远程控制 === '已验证';
        d.fields = { ...d.fields, capabilityVersionUuid: v.uuid };
        h.touch(d, s);
      }
    } else reject('能力动作无效');
    event(target, `能力${f.stage}`);
    return target;
  }
  if (op === 'repair') {
    if (target?.kind !== 'repair') reject('请选择维修单');
    const d = h.get(s, target.fields?.deviceUuid, 'device');
    const step = f.step;
    const next: Record<string, [string[], string]> = {
      assign: [['open'], 'inProgress'],
      diagnose: [['inProgress'], 'inProgress'],
      resolve: [['inProgress'], 'resolved'],
      accept: [['resolved'], 'closed'],
      reject: [['resolved'], 'inProgress'],
      reopen: [['closed'], 'open'],
    };
    if (!next[step]?.[0].includes(target.status))
      reject('维修阶段不允许此动作', 'INVALID_TRANSITION');
    const note = required(input.note, '处理说明');
    if (step === 'resolve' && !target.fields?.diagnosis) reject('请先登记诊断');
    if (step === 'accept' && !target.result) reject('缺少维修结果，不能关闭');
    if (
      step === 'reopen' &&
      (d.status !== 'stock' || pendingCollectionCount(s, d.uuid))
    )
      reject('设备已再次领用或有未结采集，不能直接重开');
    if (step === 'assign') {
      if (!teachers.includes(input.memberUuid || '')) reject('请选择处理人');
      target.owner = input.memberUuid;
    }
    if (step === 'diagnose')
      target.fields = { ...target.fields, diagnosis: note };
    if (step === 'resolve') target.result = note;
    if (step === 'accept' || step === 'reopen') {
      d.status = step === 'accept' ? 'stock' : 'repair';
      h.touch(d, s);
    }
    target.status = next[step][1];
    event(target, `${step}：${note}`);
    const ticket = h.get(s, target.fields?.ticketUuid, 'ticket');
    ticket.status = target.status;
    ticket.owner = target.owner;
    ticket.result = target.result;
    h.touch(ticket, s);
    return target;
  }
  const row = device();
  const at = input.startAt || s.now;
  if (!Number.isFinite(Date.parse(at)) || Date.parse(at) > Date.parse(s.now))
    reject('生效时间无效或晚于当前业务时间');
  const note = required(input.note, '操作原因');
  if (['assign', 'borrow'].includes(op)) {
    if (row.status !== 'stock') reject('设备不在库', 'INVALID_TRANSITION');
    bind(row, input.memberUuid || '', f.campus || row.campus, at, op);
  } else if (op === 'return') {
    if (row.status !== 'assigned') reject('设备未领用');
    if (pendingCollectionCount(s, row.uuid)) reject('存在未结采集，请先处理');
    endBinding(row, at);
  } else if (op === 'transfer') {
    if (row.status !== 'assigned') reject('仅已领用设备可调拨');
    if (pendingCollectionCount(s, row.uuid)) reject('存在未结采集，请先处理');
    endBinding(row, at);
    bind(row, input.memberUuid || '', f.campus, at, op);
  } else if (op === 'replace') {
    const replacement = h.get(s, f.replacementUuid, 'device');
    if (
      replacement.status !== 'stock' ||
      replacement.uuid === row.uuid ||
      replacement.campus !== row.campus
    )
      reject('替换设备须同校区且在库');
    if (f.replacementRevision !== String(replacement.revision))
      reject('替换设备版本已变化，请重新预览', 'CONFLICT');
    if (
      pendingCollectionCount(s, row.uuid) ||
      pendingCollectionCount(s, replacement.uuid)
    )
      reject('存在未结采集，请先处理');
    const repair = s.entities.find(
      (e) =>
        e.kind === 'repair' &&
        e.fields?.deviceUuid === row.uuid &&
        !['closed'].includes(e.status),
    );
    if (repair?.fields?.replacementUuid)
      reject('本维修已换机，不能重复执行', 'CONFLICT');
    if (
      row.status !== 'assigned' &&
      !(row.status === 'repair' && repair?.fields?.previousMemberUuid)
    )
      reject('原设备无待替换使用人');
    const memberUuid =
      row.status === 'assigned'
        ? row.memberUuid
        : repair?.fields?.previousMemberUuid || '';
    if (row.status === 'assigned') endBinding(row, at);
    bind(replacement, memberUuid, row.campus, at, 'replace');
    h.touch(replacement, s);
    if (repair) {
      repair.fields = { ...repair.fields, replacementUuid: replacement.uuid };
      h.touch(repair, s);
    }
    event(replacement, `替换${row.title}，边界${at}，旧录音归属保持历史快照`);
  } else if (op === 'report') {
    if (!['stock', 'assigned'].includes(row.status))
      reject('当前设备不能重复报修');
    const previousMemberUuid = row.status === 'assigned' ? row.memberUuid : '';
    if (row.status === 'assigned') endBinding(row, at);
    row.status = 'repair';
    const repair = h.append(s, 'repair', `${row.title}维修`, {
      status: 'open',
      campus: row.campus,
      memberUuid: row.memberUuid,
      note,
      fields: {
        deviceUuid: row.uuid,
        previousMemberUuid,
        faultCategory: f.faultCategory || '其他',
      },
    });
    const ticket = h.append(s, 'ticket', `${row.title}故障工单`, {
      status: 'open',
      campus: row.campus,
      sourceUuid: repair.uuid,
      note,
      fields: { repairUuid: repair.uuid, deviceUuid: row.uuid },
    });
    repair.fields = { ...repair.fields, ticketUuid: ticket.uuid };
    event(repair, note);
  } else if (['disable', 'retire', 'enable'].includes(op)) {
    if (
      op === 'enable'
        ? row.status !== 'disabled'
        : !['stock', ...(op === 'retire' ? ['disabled'] : [])].includes(
            row.status,
          )
    )
      reject('设备状态不允许此操作，请先归还/验收', 'INVALID_TRANSITION');
    if (
      s.entities.some(
        (b) => b.kind === 'binding' && b.deviceUuid === row.uuid && !b.endAt,
      ) ||
      pendingCollectionCount(s, row.uuid) ||
      s.entities.some(
        (r) =>
          r.kind === 'repair' &&
          r.fields?.deviceUuid === row.uuid &&
          r.status !== 'closed',
      )
    )
      reject('设备有绑定、未结采集或维修，不能停用/报废');
    row.status =
      op === 'enable' ? 'stock' : op === 'retire' ? 'retired' : 'disabled';
  } else if (op === 'remote') {
    const model = s.entities.find(
      (e) => e.kind === 'model' && e.title === row.model,
    );
    if (
      !['stock', 'assigned'].includes(row.status) ||
      capabilities(model).远程控制 !== '已验证'
    )
      reject('远程能力未验证或设备不可用', 'CAPABILITY_UNAVAILABLE');
    const log = h.append(s, 'task', `${row.title}模拟远程配置`, {
      status: 'completed',
      campus: row.campus,
      sourceUuid: row.uuid,
      note,
      fields: {
        capabilityVersionUuid:
          model?.fields?.publishedVersion || `legacy:${model?.uuid}:1`,
        simulated: 'true',
      },
    });
    event(row, `模拟远程配置，版本${log.fields?.capabilityVersionUuid}`);
  } else reject('设备动作无效');
  event(row, `${note}；生效${at}`);
  return row;
}
